import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchText$ from '@atlassian/jira-fetch/src/utils/as-text-stream.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { PACKAGE_NAME } from '@atlassian/jira-servicedesk-getting-started-panel-common/src/constants.tsx';
import { withExponentialBackoffRetries } from '@atlassian/jira-servicedesk-getting-started-panel-common/src/utils/with-exponential-backoff-retries/index.tsx';
import type { BaseUrl, CloudId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { GspState } from '@atlassian/jsm-getting-started-panel';
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import type { State, ActionProps } from '../../types.tsx';

export type SavePanelState = (arg1: GspState) => Promise<void>;

const getUrl = ({ baseUrl, cloudId }: { baseUrl: BaseUrl; cloudId: CloudId }): string =>
	`${baseUrl}/jsm/onboarding/checklist/v1/cloudid/${cloudId}/user/list/jsm-getting-started-panel/properties`;
export const savePanelState =
	({ baseUrl, cloudId, createAnalyticsEvent }: ActionProps) =>
	({ getState, setState }: StoreActionApi<State>): SavePanelState =>
	async (state: GspState) => {
		const { data } = getState();

		try {
			if (!data) {
				throw new Error('Trying to save panel state without current GSP state.');
			}
			// Set the state locally without waiting for the API call
			setState({
				data: state,
				isSaving: true,
			});

			const { sectionState, activeState, visualState, projectId, hasSeenReopenSpotlight } =
				state.properties.user;

			await withExponentialBackoffRetries({
				callback: () =>
					fetchText$(getUrl({ baseUrl, cloudId }), {
						method: 'POST',
						credentials: 'include',
						body: JSON.stringify({
							sectionState,
							activeState,
							visualState,
							projectId,
							hasSeenReopenSpotlight,
						}),
					}).toPromise(),
				totalAttempts: 5,
				baseIntervalMs: 500,
			});

			setState({ isSaving: false });
			fireTrackAnalytics(createAnalyticsEvent({}), `${PACKAGE_NAME}.savePanelState succeeded`);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setState({ isSaving: false });
			fireErrorAnalytics({
				error,
				meta: {
					id: 'savePanelState',
					packageName: PACKAGE_NAME,
					teamName: 'itops-mithril',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			throw error;
		}
	};
